import React, { useState, useEffect, useCallback } from 'react';
import { FiEdit2, FiUserX, FiUserCheck, FiLogIn, FiLink } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TableFilters, { SortableTh } from '../components/TableFilters';
import './AdminUsuarios.css';

const AdminUsuarios = () => {
  const { user, startImpersonation } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [total, setTotal] = useState(0);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterAtivo, setFilterAtivo] = useState('');
  const [sort, setSort] = useState('nome');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    empresa: '',
    plano: 'start',
    role: 'visualizador',
    ativo: true,
    senha: ''
  });

  // Modal de vínculos
  const [showVinculoModal, setShowVinculoModal] = useState(false);
  const [vinculoUsuario, setVinculoUsuario] = useState(null);
  const [vinculoEmpreendimentos, setVinculoEmpreendimentos] = useState([]);
  const [vinculoGrupos, setVinculoGrupos] = useState([]);
  const [vinculoEmpreendimentosSemGrupo, setVinculoEmpreendimentosSemGrupo] = useState([]);
  const [vinculoLicencas, setVinculoLicencas] = useState([]);
  const [vinculoCheckedEmp, setVinculoCheckedEmp] = useState(new Set());
  const [vinculoCheckedLic, setVinculoCheckedLic] = useState(new Set());
  const [vinculoLoading, setVinculoLoading] = useState(false);
  const [vinculoSaving, setVinculoSaving] = useState(false);

  const carregarRoles = useCallback(async () => {
    const rolesRes = await api.get('/api/usuarios/roles');
    setRoles(rolesRes.data || []);
  }, []);

  const carregarUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (filterRole) params.set('role', filterRole);
      if (filterAtivo !== '') params.set('ativo', filterAtivo);
      params.set('sort', sort);
      params.set('order', order);
      params.set('page', page);
      params.set('limit', limit);
      const usersRes = await api.get(`/api/usuarios?${params}`);
      const data = usersRes.data;
      if (data && typeof data === 'object' && 'data' in data) {
        setUsuarios(data.data || []);
        setTotal(data.total || 0);
      } else {
        setUsuarios(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [search, filterRole, filterAtivo, sort, order, page, limit]);

  useEffect(() => { carregarRoles(); }, [carregarRoles]);
  useEffect(() => { carregarUsuarios(); }, [carregarUsuarios]);

  const carregarDados = () => carregarUsuarios();

  const handleSort = (col) => {
    setSort(col);
    setOrder((o) => (sort === col && o === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const dataToSend = { ...formData };
        // Remover senha se estiver vazia (não alterar)
        if (!dataToSend.senha || dataToSend.senha.trim() === '') {
          delete dataToSend.senha;
        }
        await api.put(`/api/usuarios/${editing}`, dataToSend);
      } else {
        await api.post('/api/usuarios', {
          ...formData,
          senha: formData.senha
        });
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ nome: '', email: '', empresa: '', plano: 'start', role: 'visualizador', ativo: true, senha: '' });
      carregarDados();
      alert(editing ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      alert(error.response?.data?.error || 'Erro ao salvar usuário');
    }
  };

  const handleEdit = (usr) => {
    setEditing(usr.id);
    setFormData({
      nome: usr.nome || '',
      email: usr.email || '',
      empresa: usr.empresa || '',
      plano: usr.plano || 'start',
      role: usr.role || 'visualizador',
      ativo: usr.ativo !== 0,
      senha: ''
    });
    setShowModal(true);
  };

  const handleToggleAtivo = async (usuarioId, atualAtivo) => {
    try {
      await api.put(`/api/usuarios/${usuarioId}`, { ativo: !atualAtivo });
      carregarDados();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      const msg = error.response?.data?.error || error.message || 'Erro ao alterar status do usuário';
      const status = error.response?.status;
      alert(status ? `[${status}] ${msg}` : msg);
    }
  };

  const handleAcessarComo = async (usr) => {
    const result = await startImpersonation(usr.id);
    if (result?.success) {
      window.location.href = '/';
    } else if (result?.error) {
      alert(result.error);
    }
  };

  const handleAbrirVinculo = async (usr) => {
    setVinculoUsuario(usr);
    setShowVinculoModal(true);
    setVinculoLoading(true);
    try {
      const [vinculosRes, opcoesRes] = await Promise.all([
        api.get(`/api/usuarios/${usr.id}/vinculos`),
        api.get('/api/admin/opcoes-vinculos')
      ]);
      const vinculos = vinculosRes.data || {};
      const empIds = new Set((vinculos.empreendimentos || []).map((e) => e.id));
      const licIds = new Set((vinculos.licencas || []).map((l) => l.id));
      setVinculoCheckedEmp(empIds);
      setVinculoCheckedLic(licIds);

      const opcoes = opcoesRes.data || {};
      setVinculoEmpreendimentos(opcoes.empreendimentos || []);
      setVinculoGrupos(opcoes.grupos || []);
      setVinculoEmpreendimentosSemGrupo(opcoes.empreendimentos_sem_grupo || []);
      setVinculoLicencas(opcoes.licencas || []);
    } catch (err) {
      console.error('Erro ao carregar vínculos:', err);
      alert(err.response?.data?.error || 'Erro ao carregar vínculos');
    } finally {
      setVinculoLoading(false);
    }
  };

  const handleToggleEmpreendimento = (id) => {
    setVinculoCheckedEmp((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleLicenca = (id) => {
    setVinculoCheckedLic((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSalvarVinculos = async () => {
    if (!vinculoUsuario) return;
    setVinculoSaving(true);
    try {
      const ROLE_DEFAULT = 'role-visualizador';
      const userId = vinculoUsuario.id;

      const vinculosAtuais = await api.get(`/api/usuarios/${userId}/vinculos`);
      const empAtuais = new Set((vinculosAtuais.data?.empreendimentos || []).map((e) => e.id));
      const licAtuais = new Set((vinculosAtuais.data?.licencas || []).map((l) => l.id));

      for (const emp of vinculoEmpreendimentos) {
        const checked = vinculoCheckedEmp.has(emp.id);
        const tinha = empAtuais.has(emp.id);
        if (checked && !tinha) {
          await api.post(`/api/usuarios/${userId}/empreendimentos`, { empreendimento_id: emp.id, role_id: ROLE_DEFAULT });
        } else if (!checked && tinha) {
          await api.delete(`/api/usuarios/${userId}/empreendimento/${emp.id}`);
        }
      }

      for (const lic of vinculoLicencas) {
        const checked = vinculoCheckedLic.has(lic.id);
        const tinha = licAtuais.has(lic.id);
        if (checked && !tinha) {
          await api.post(`/api/usuarios/${userId}/licencas`, { licenca_id: lic.id, role_id: ROLE_DEFAULT });
        } else if (!checked && tinha) {
          await api.delete(`/api/usuarios/${userId}/licencas/${lic.id}`);
        }
      }

      setShowVinculoModal(false);
      setVinculoUsuario(null);
      alert('Vínculos atualizados com sucesso!');
    } catch (err) {
      console.error('Erro ao salvar vínculos:', err);
      alert(err.response?.data?.error || 'Erro ao salvar vínculos');
    } finally {
      setVinculoSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  const isAdmin = user?.role === 'admin_sistema' || user?.email === 'admin@greencheck.com';
  if (!isAdmin) {
    return <div className="error">Acesso negado. Apenas administradores podem acessar esta página.</div>;
  }

  return (
    <div className="admin-usuarios-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Usuários</h1>
          <p className="page-subtitle">Ver todos os usuários cadastrados. Adicione usuários de Gestão Green Check ou Administradores sem vincular a empreendimento.</p>
        </div>
        <button className="btn btn-primary" onClick={() => {
          setEditing(null);
          setFormData({ nome: '', email: '', empresa: '', plano: 'start', role: 'visualizador', ativo: true, senha: '' });
          setShowModal(true);
        }}>
          + Novo Usuário
        </button>
      </div>

      <TableFilters
        search={search}
        onSearchChange={setSearch}
        filters={[
          {
            key: 'role',
            label: 'Papel',
            value: filterRole,
            onChange: (v) => { setFilterRole(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(roles.map((r) => ({ value: r.nome, label: r.nome })))
            ]
          },
          {
            key: 'ativo',
            label: 'Status',
            value: filterAtivo,
            onChange: (v) => { setFilterAtivo(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              { value: '1', label: 'Ativo' },
              { value: '0', label: 'Inativo' }
            ]
          }
        ]}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        searchPlaceholder="Buscar por nome ou email..."
      />

      {usuarios.length === 0 ? (
        <div className="empty-state">
          <p>Nenhum usuário encontrado.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
            <tr>
                <SortableTh sortKey="nome" currentSort={sort} currentOrder={order} onSort={handleSort}>Nome</SortableTh>
                <SortableTh sortKey="email" currentSort={sort} currentOrder={order} onSort={handleSort}>Email</SortableTh>
                <SortableTh sortKey="empresa" currentSort={sort} currentOrder={order} onSort={handleSort}>Empresa</SortableTh>
                <th>Empreendimentos</th>
                <th>Licenças</th>
                <th>Plano</th>
                <SortableTh sortKey="role" currentSort={sort} currentOrder={order} onSort={handleSort}>Role</SortableTh>
                <th>Status</th>
                <th>Ações</th>
            </tr>
            </thead>
            <tbody>
              {usuarios.map((usr) => (
                <tr key={usr.id}>
                  <td>{usr.nome}</td>
                  <td>{usr.email}</td>
                  <td>{usr.empresa || '-'}</td>
                  <td>{usr.empreendimentos_nomes || '-'}</td>
                  <td>{usr.licencas_nomes || '-'}</td>
                  <td>
                    <span className="badge badge-info">{usr.plano || 'start'}</span>
                  </td>
                  <td>
                    <span className="badge badge-secondary">
                      {usr.roles_empreendimento_nomes
                        ? usr.roles_empreendimento_nomes
                        : (usr.role_descricao_global || usr.role || 'visualizador')}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${usr.ativo !== 0 ? 'badge-success' : 'badge-danger'}`}>
                      {usr.ativo !== 0 ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {usr.role !== 'admin_sistema' && usr.role !== 'gestao_greencheck' && (
                        <button
                          className="btn btn-sm btn-outline btn-icon"
                          onClick={() => handleAbrirVinculo(usr)}
                          title="Gerenciar vínculos (empreendimentos e licenças)"
                        >
                          <FiLink />
                        </button>
                      )}
                      {usr.id !== user?.id && usr.role !== 'admin_sistema' && usr.ativo !== 0 && (
                        <button
                          className="btn btn-sm btn-info btn-icon"
                          onClick={() => handleAcessarComo(usr)}
                          title="Acessar como este usuário"
                        >
                          <FiLogIn />
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-secondary btn-icon"
                        onClick={() => handleEdit(usr)}
                        title="Editar"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className={`btn btn-sm btn-icon ${usr.ativo !== 0 ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleToggleAtivo(usr.id, usr.ativo !== 0)}
                        title={usr.ativo !== 0 ? 'Desativar' : 'Ativar'}
                      >
                        {usr.ativo !== 0 ? <FiUserX /> : <FiUserCheck />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setEditing(null);
          setFormData({ nome: '', email: '', empresa: '', plano: 'start', role: 'visualizador', ativo: true, senha: '' });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? 'Editar' : 'Novo'} Usuário</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!editing}
                />
              </div>
              <div className="form-group">
                <label>Empresa</label>
                <input
                  type="text"
                  value={formData.empresa}
                  onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Plano</label>
                  <select
                    value={formData.plano}
                    onChange={(e) => setFormData({ ...formData, plano: e.target.value })}
                  >
                    <option value="start">Start</option>
                    <option value="professional">Professional</option>
                    <option value="corporate">Corporate</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.nome}>
                        {role.descricao}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  />
                  {' '}Usuário Ativo
                </label>
              </div>
              {!editing && (
                <div className="form-group">
                  <label>Senha *</label>
                  <input
                    type="password"
                    required
                    value={formData.senha || ''}
                    onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  />
                </div>
              )}
              {editing && (
                <div className="form-group">
                  <label>Nova Senha (deixe em branco para manter a atual)</label>
                  <input
                    type="password"
                    value={formData.senha || ''}
                    onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  />
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                  setFormData({ nome: '', email: '', empresa: '', plano: 'start', role: 'visualizador', ativo: true, senha: '' });
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editing ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showVinculoModal && vinculoUsuario && (
        <div className="modal-overlay" onClick={() => !vinculoSaving && setShowVinculoModal(false)}>
          <div className="modal-content modal-vinculos" onClick={(e) => e.stopPropagation()}>
            <h2>
              <FiLink /> Vínculos — {vinculoUsuario.nome}
            </h2>
            <p className="modal-vinculos-desc">
              Marque os empreendimentos e licenças aos quais este usuário tem acesso. Vínculo em empreendimento dá acesso total; vínculo em licença restringe ao escopo da licença.
            </p>

            {vinculoLoading ? (
              <div className="vinculos-loading">Carregando…</div>
            ) : (
              <>
                <div className="vinculos-section">
                  <h3>Empreendimentos</h3>
                  {vinculoEmpreendimentos.length === 0 ? (
                    <p className="vinculos-empty">Nenhum empreendimento cadastrado.</p>
                  ) : (
                    <>
                      {vinculoGrupos.filter((g) => (g.empreendimentos || []).length > 0).map((grupo) => (
                        <div key={grupo.id} className="vinculos-grupo-empresarial">
                          <h4 className="vinculos-grupo-titulo">{grupo.nome}</h4>
                          <div className="vinculos-grupo-itens">
                            {(grupo.empreendimentos || []).map((emp) => (
                              <label key={emp.id} className="vinculos-item">
                                <input
                                  type="checkbox"
                                  checked={vinculoCheckedEmp.has(emp.id)}
                                  onChange={() => handleToggleEmpreendimento(emp.id)}
                                />
                                <span>{emp.nome || emp.id}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                      {vinculoEmpreendimentosSemGrupo.length > 0 && (
                        <div className="vinculos-grupo-empresarial">
                          <h4 className="vinculos-grupo-titulo">Outros empreendimentos</h4>
                          <div className="vinculos-grupo-itens">
                            {vinculoEmpreendimentosSemGrupo.map((emp) => (
                              <label key={emp.id} className="vinculos-item">
                                <input
                                  type="checkbox"
                                  checked={vinculoCheckedEmp.has(emp.id)}
                                  onChange={() => handleToggleEmpreendimento(emp.id)}
                                />
                                <span>{emp.nome || emp.id}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="vinculos-section">
                  <h3>Licenças</h3>
                  <div className="vinculos-grupo-itens">
                    {vinculoLicencas.length === 0 ? (
                      <p className="vinculos-empty">Nenhuma licença cadastrada.</p>
                    ) : (
                      vinculoLicencas.map((lic) => (
                        <label key={lic.id} className="vinculos-item">
                          <input
                            type="checkbox"
                            checked={vinculoCheckedLic.has(lic.id)}
                            onChange={() => handleToggleLicenca(lic.id)}
                          />
                          <span>
                            {lic.numero || lic.tipo || lic.id}
                            {lic.empreendimento_nome && ` — ${lic.empreendimento_nome}`}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowVinculoModal(false)}
                    disabled={vinculoSaving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSalvarVinculos}
                    disabled={vinculoSaving}
                  >
                    {vinculoSaving ? 'Salvando…' : 'Salvar vínculos'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsuarios;
